import axios from "axios";
import {
  makeAutoObservable,
  action,
  observable,
  computed,
  runInAction,
} from "mobx";
import { get, eq, some } from "lodash";
import { API_HOST, RequestState, defaultErrorMessage } from "../constants";
import { getAge, isValidEmail } from "../utils";

export class RegisterController {
  registerMessage = "";
  registerRequestState = RequestState.IDLE;

  // Form state
  formData = {
    email: "",
    username: "",
    firstName: "",
    lastName: "",
    dob: "",
    gender: "",
    country: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  };

  errors = {};
  validationStatus = {};
  activeStep = 0;
  passwordStrength = 0;
  showTermsModal = false;

  steps = ["Account Info", "Personal Details", "Security"];

  constructor() {
    makeAutoObservable(this, {
      registerMessage: observable,
      registerRequestState: observable,
      formData: observable,
      errors: observable,
      validationStatus: observable,
      activeStep: observable,
      passwordStrength: observable,
      showTermsModal: observable,
      register: action,
      updateFormData: action,
      validateField: action,
      nextStep: action,
      previousStep: action,
      setShowTermsModal: action,
      resetForm: action,
      getRegisterMessage: computed,
      getRegisterRequestState: computed,
    });
  }

  // Form management methods
  updateFormData = (name, value) => {
    runInAction(() => {
      let newData = Object.assign({}, this.formData);
      newData[name] = value;
      this.formData = newData;

      // Clear error when user starts typing
      if (this.errors[name]) {
        this.errors[name] = "";
      }
    });

    // Real-time validation
    this.validateField(name, value);
  };

  calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  validateField = (fieldName, value) => {
    let isValid = false;
    let errorMessage = "";

    switch (fieldName) {
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        isValid = emailRegex.test(value);
        if (!value) errorMessage = "Email is required";
        else if (!isValid) errorMessage = "Please enter a valid email address";
        break;
      case "username":
        isValid =
          value.length >= 3 &&
          value.length <= 20 &&
          /^[a-zA-Z0-9_]+$/.test(value);
        if (!value) errorMessage = "Username is required";
        else if (value.length < 3)
          errorMessage = "Username must be at least 3 characters";
        else if (value.length > 20)
          errorMessage = "Username must be less than 20 characters";
        else if (!/^[a-zA-Z0-9_]+$/.test(value))
          errorMessage =
            "Username can only contain letters, numbers, and underscores";
        break;
      case "firstName":
        isValid = value.length >= 2 && /^[a-zA-Z\s]+$/.test(value);
        if (!value) errorMessage = "First name is required";
        else if (value.length < 2)
          errorMessage = "First name must be at least 2 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value))
          errorMessage = "First name can only contain letters";
        break;
      case "lastName":
        isValid = value.length >= 2 && /^[a-zA-Z\s]+$/.test(value);
        if (!value) errorMessage = "Last name is required";
        else if (value.length < 2)
          errorMessage = "Last name must be at least 2 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value))
          errorMessage = "Last name can only contain letters";
        break;
      case "dob":
        const birthDate = new Date(value);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        isValid = value && age >= 13 && age <= 120;
        if (!value) errorMessage = "Date of birth is required";
        else if (age < 13) errorMessage = "You must be at least 13 years old";
        else if (age > 120) errorMessage = "Please enter a valid date of birth";
        break;
      case "password":
        const strength = this.calculatePasswordStrength(value);
        runInAction(() => {
          this.passwordStrength = strength;
        });
        isValid = value.length >= 8 && strength >= 3;
        if (!value) errorMessage = "Password is required";
        else if (value.length < 8)
          errorMessage = "Password must be at least 8 characters";
        else if (strength < 3) errorMessage = "Password is too weak";
        break;
      case "confirmPassword":
        isValid = value === this.formData.password;
        if (!value) errorMessage = "Please confirm your password";
        else if (!isValid) errorMessage = "Passwords do not match";
        break;
      default:
        isValid = !!value;
        if (!value) errorMessage = `${fieldName} is required`;
    }

    runInAction(() => {
      this.validationStatus[fieldName] = isValid;
      if (errorMessage) {
        this.errors[fieldName] = errorMessage;
      } else {
        this.errors[fieldName] = "";
      }
    });
  };

  validateStep = (step) => {
    const fieldsToValidate = [];

    switch (step) {
      case 0: // Account Info
        fieldsToValidate.push("email", "username");
        break;
      case 1: // Personal Details
        fieldsToValidate.push(
          "firstName",
          "lastName",
          "dob",
          "gender",
          "country"
        );
        break;
      case 2: // Security
        fieldsToValidate.push("password", "confirmPassword");
        if (!this.formData.acceptTerms) {
          this.errors.acceptTerms = "You must accept the terms of service";
          return false;
        }
        break;
      default:
        break;
    }

    // Validate all fields for this step
    fieldsToValidate.forEach((field) => {
      this.validateField(field, this.formData[field]);
    });

    const stepValid = fieldsToValidate.every(
      (field) => this.validationStatus[field]
    );
    return stepValid && (step !== 2 || this.formData.acceptTerms);
  };

  nextStep = () => {
    if (this.validateStep(this.activeStep)) {
      runInAction(() => {
        this.activeStep = Math.min(this.activeStep + 1, this.steps.length - 1);
      });
    }
  };

  previousStep = () => {
    runInAction(() => {
      this.activeStep = Math.max(this.activeStep - 1, 0);
    });
  };

  setShowTermsModal = (show) => {
    runInAction(() => {
      this.showTermsModal = show;
    });
  };

  resetForm = () => {
    runInAction(() => {
      this.formData = {
        email: "",
        username: "",
        firstName: "",
        lastName: "",
        dob: "",
        gender: "",
        country: "",
        password: "",
        confirmPassword: "",
        acceptTerms: false,
      };
      this.errors = {};
      this.validationStatus = {};
      this.activeStep = 0;
      this.passwordStrength = 0;
      this.registerMessage = "";
    });
  };

  async register(__registerSuccessCallback) {
    const {
      email,
      username,
      firstName,
      lastName,
      dob,
      gender,
      password,
      confirmPassword,
      country,
    } = this.formData;

    runInAction(() => {
      this.registerMessage = "";
      this.registerRequestState = RequestState.LOADING;
    });

    if (
      some([
        !email.length,
        !username.length,
        !firstName.length,
        !lastName.length,
        !dob.length,
        !gender.length,
        !password.length,
        !confirmPassword.length,
        !country.length,
      ])
    ) {
      runInAction(() => {
        this.registerMessage = "Please fill all fields.";
      });
      return;
    }

    if (!eq(password, confirmPassword)) {
      runInAction(() => {
        this.registerMessage = "Passwords do not match";
      });
      return;
    }

    const age = getAge(dob);

    if (age < 18) {
      runInAction(() => {
        this.registerMessage =
          "You must be at least 18 years of age to sign up";
      });
      return;
    }

    if (!isValidEmail(email)) {
      runInAction(() => {
        this.registerMessage = "Please enter a valid email address";
      });
      return;
    }

    runInAction(() => {
      this.registerRequestState = RequestState.REQUESTED;
    });

    try {
      const response = await axios.post(`${API_HOST}user/create_user`, {
        email,
        username,
        password,
        first_name: firstName,
        last_name: lastName,
        dob: dob,
        gender,
        country,
      });

      if (eq(response.status, 200)) {
        runInAction(() => {
          this.registerMessage = "";
        });

        __registerSuccessCallback();
      } else {
        runInAction(() => {
          this.registerMessage = get(
            response,
            "data.message",
            defaultErrorMessage
          );
        });
      }
    } catch (error) {
      console.log("Error creating user", error);

      runInAction(() => {
        this.registerMessage = get(
          error,
          "response.data.error",
          defaultErrorMessage
        );
      });
    }

    runInAction(() => {
      this.registerRequestState = RequestState.RECEIVED;
    });
  }

  get getRegisterMessage() {
    return this.registerMessage;
  }

  get getRegisterRequestState() {
    return this.registerRequestState;
  }
}

export default RegisterController;
